import axios, { AxiosRequestConfig } from "axios";
import { loadState, saveState } from "src/redux/store/localStorage";
import CacheBuster from "src/cacheBuster.js";
import useGoogleAnalytics from "src/utils/GoogleAnalytics";
import { HistoryLike, redirectOnRequestFailure } from "src/utils/requestError";

declare module "axios" {
    export interface AxiosRequestConfig {
        redirectOnFailure?: boolean;
        __isRetryRequest?: boolean;
    }
}

export const getToken = () => localStorage.getItem("_MN_TOKEN");
export const setToken = (token: string) => localStorage.setItem("_MN_TOKEN", token);
export const removeToken = () => localStorage.removeItem("_MN_TOKEN");

const client = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
});

client.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        return {
            ...config,
            headers: {
                ...config.headers,
                authorization: `Bearer ${token}`,
            },
        };
    }
    return config;
});

let responseInterceptorId: number | null = null;

const skipsSessionRefresh = (url?: string) => {
    if (!url) {
        return false;
    }
    return (
        url.includes("/auth/signin") ||
        url.includes("/auth/signup") ||
        url.includes("/auth/verify") ||
        url.includes("/auth/forgotpassword") ||
        url.includes("/auth/verifypassword") ||
        url.includes("/auth/tokensignin") ||
        url.includes("/auth/refresh") ||
        /\/auth\/[^/?]+\/exists/.test(url)
    );
};

export const useSetupInterceptors = () => {
    const { logError } = useGoogleAnalytics();

    const setupInterceptors = (history: HistoryLike) => {
        if (responseInterceptorId !== null) {
            client.interceptors.response.eject(responseInterceptorId);
        }

        responseInterceptorId = client.interceptors.response.use(
            (response) => {
                // decide if to clear cache
                const { loading, isLatestVersion, refreshCacheAndReload } = CacheBuster(
                    response.headers["frontend-build-version"],
                );

                if (!loading && !isLatestVersion) {
                    refreshCacheAndReload();
                }

                return response;
            },
            (error) => {
                const originalRequest = error.config as AxiosRequestConfig | undefined;
                const refreshToken = localStorage.getItem("refresh_token");
                const status = error.response?.status;

                if (
                    error.response &&
                    (status === 401 || status === 500) &&
                    originalRequest &&
                    !originalRequest.__isRetryRequest &&
                    refreshToken &&
                    !skipsSessionRefresh(originalRequest.url)
                ) {
                    originalRequest.__isRetryRequest = true;

                    return fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            refreshToken: refreshToken,
                        }),
                    })
                        .then((res) => res.json())
                        .then((res) => {
                            let state = loadState();
                            if (!state) {
                                state = {};
                            }
                            if (!state.login) {
                                state.login = {};
                            }
                            state.login.data = res;
                            state.login.data.expiry =
                                Date.now() + res.cognitoJWT.expires_in * 1000;
                            saveState(state);
                            setToken(res.cognitoJWT.access_token);
                            window.location.reload();
                            return axios(originalRequest);
                        })
                        .catch(() => {
                            let state = loadState();
                            if (!state) {
                                state = {};
                            }
                            state.login = {};
                            state.signup = {};
                            state.userInfo = {};
                            saveState(state);
                            removeToken();
                            localStorage.removeItem("refresh_token");
                            window.location.reload();
                            return axios(originalRequest);
                        });
                }

                logError({
                    description: error.message || "Network Error",
                    fatal: false,
                    traceback: error.stack ? error.stack : error.toString(),
                });
                if (originalRequest?.redirectOnFailure) {
                    redirectOnRequestFailure(history, error);
                }
                return Promise.reject(error.response || error);
            },
        );
    };
    return { setupInterceptors };
};

export { client };
